import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ContentControlStudioVODTimelineComponent } from '../content-control-studio-timeline/content-control-studio-vod-timeline.component';
import { ContentControlStudioVODViewModel } from '../content-control-studio-vod-view-model';
import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';

interface VodTimelineSnapshot {
  timelineBlockDurationSecs: number;
  isVideoTimelineFit: boolean;
  isFitSegment: boolean;
  selectedSegmentId: number | undefined;
  timelineMode: ETimelineMode;
  oneFrameSecs: number;
  videoFps: number;
  pixelsPerSecond: number;
  currentVideoTime: number;
  currentVideoPlayTimeSecs: number;
  currentVideoPlayTimeInMs: number;

}

interface VodViewModelSnapshot {
  showDebugPanel: boolean;
  segmentsCount: number;
  isAudioMuted: boolean;
  isAudioForcedMuted: boolean;
  selectedAudioOption: string;
  timelineMode: ETimelineMode;
  isFitSegment: boolean;
}

@Component({
  selector: 'evrideo-vod-debug-panel',
  templateUrl: './content-control-studio-vod-debug-panel.component.html',
  styleUrls: ['./content-control-studio-vod-debug-panel.component.scss'],
})
export class ContentControlStudioVodDebugPanelComponent implements OnInit, OnDestroy {
  @Input() vodTimeline: ContentControlStudioVODTimelineComponent;
  @Input() vodVM: ContentControlStudioVODViewModel;
  @Output() closed = new EventEmitter<void>();

  timelineSnapshot: VodTimelineSnapshot;
  vmSnapshot: VodViewModelSnapshot;
  collapsedSections: Record<string, boolean> = { timeline: true, vm: true };
  posX = 20;
  posY = 80;

  public readonly tooltips: Record<string, string> = {
    // VOD Timeline
    timelineBlockDurationSecs: 'Duration of each ruler block on the timeline in seconds.',
    isVideoTimelineFit: 'Whether the timeline is zoomed to fit the entire video duration.',
    isFitSegment: 'Whether the timeline is zoomed to fit the selected segment.',
    selectedSegmentId: 'ID of the currently selected editing directive segment.',
    timelineMode: 'Current timeline display mode (OriginalTime or EditedTime).',
    oneFrameSecs: 'Duration of a single frame in seconds, derived from videoFps.',
    videoFps: 'Frame rate of the video stream (frames per second).',
    pixelsPerSecond: 'Current pixel density — pixels per second of video time on the timeline.',
    currentVideoTime: 'Visible timeline duration in seconds (may exclude cut segments in ET mode).',
    currentVideoPlayTimeSecs: 'Current playback position in seconds from the video source.',
    currentVideoPlayTimeInMs: 'Current playback time in milliseconds for UI display.',
    // VOD ViewModel
    showDebugPanel: 'Whether the debug panel overlay is visible.',
    segmentsCount: 'Number of editing directive segments.',
    isAudioMuted: 'Whether the audio is currently muted by user toggle.',
    isAudioForcedMuted: 'Whether the audio is force-muted by the system (e.g. during seek).',
    selectedAudioOption: 'Currently selected audio track option name.',
  };

  private _interval: ReturnType<typeof setInterval>;
  private _dragging = false;
  private _dragOffsetX = 0;
  private _dragOffsetY = 0;

  ngOnInit(): void {
    this._updateSnapshot();
    this._interval = setInterval(() => this._updateSnapshot(), 250);
  }

  ngOnDestroy(): void {
    if (this._interval) {
      clearInterval(this._interval);
    }
  }

  onHeaderMouseDown(event: MouseEvent): void {
    this._dragging = true;
    this._dragOffsetX = event.clientX - this.posX;
    this._dragOffsetY = event.clientY - this.posY;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this._dragging) return;
    this.posX = event.clientX - this._dragOffsetX;
    this.posY = event.clientY - this._dragOffsetY;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this._dragging = false;
  }

  toggleSection(section: string): void {
    this.collapsedSections[section] = !this.collapsedSections[section];
  }

  formatSecsTimecode(secs: number): string {
    if (secs == null || !Number.isFinite(secs)) return 'N/A';
    const sign = secs < 0 ? '-' : '';
    const abs = Math.abs(secs);
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = Math.floor(abs % 60);
    const ms = Math.round((abs % 1) * 1000);
    return `${sign}${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }

  formatMsTimecode(ms: number): string {
    if (ms == null || !Number.isFinite(ms)) return 'N/A';
    return this.formatSecsTimecode(ms / 1000);
  }

  private _updateSnapshot(): void {
    this._updateTimelineSnapshot();
    this._updateVmSnapshot();
  }

  private _updateTimelineSnapshot(): void {
    if (!this.vodTimeline) return;
    const t = this.vodTimeline;
    this.timelineSnapshot = {
      timelineBlockDurationSecs: t.timelineBlockDurationSecs,
      isVideoTimelineFit: t.isVideoTimelineFit,
      isFitSegment: t.isFitSegment,
      selectedSegmentId: t.selectedSegmentId,
      timelineMode: t.vm.timelineMode,
      oneFrameSecs: t.oneFrameSecs,
      videoFps: t.videoFps,
      pixelsPerSecond: t.pixelsPerSecond(),
      currentVideoTime: t.currentVideoTimeSecs(t.isFitSegment, t.isEditedTimeline),
      currentVideoPlayTimeSecs: t.currentVideoPlayTimeSecs(),
      currentVideoPlayTimeInMs: t.currentVideoPlayTimeInMs(),
      
    };
  }

  private _updateVmSnapshot(): void {
    if (!this.vodVM) return;
    const vm = this.vodVM;
    this.vmSnapshot = {
      showDebugPanel: vm.showDebugPanel,
      segmentsCount: vm.segments?.length ?? 0,
      isAudioMuted: vm.isAudioMuted,
      isAudioForcedMuted: vm.isAudioForcedMuted,
      selectedAudioOption: vm.selectedAudioOption,
      timelineMode: vm.timelineMode,
      isFitSegment: vm.isFitSegment,
    };
  }

  private _safeCall(fn: () => string): string {
    try {
      return fn();
    } catch {
      return 'N/A';
    }
  }
}
